"use client"

import * as React from "react"
import { Entries } from "type-fest"

import copyTextToClipboard from "@/lib/copyTextToClipboard"
import useTransientAnimation from "@/lib/useTransientAnimation"
import type { CitationFormat } from "@/lib/store"
import { getCitePageURL } from "@/lib/urls"

import * as styles from "./Citation.css"
import CodeBlock from "./CodeBlock"
import QuoteBlock from "./QuoteBlock"
import Button from "./system/Button"
import {
	CopyToClipboard as CopyToClipboardIcon,
	Download as DownloadIcon,
} from "./system/icons"
import Link from "./system/Link"
import { Stack } from "./system/Stack"
import Text from "./system/Text"

const formatEntries = {
	biblatex: { label: "Bib", fileExtension: "bib" },
	enw: { label: "EndNote", fileExtension: "enw" },
	ris: { label: "RIS", fileExtension: "ris" },
} as const satisfies Record<CitationFormat, unknown>
type Format = keyof typeof formatEntries

type CitationProps = {
	value: string
	publicationId: string
	format: Format
	type: "text" | "code"
	linksReplaceHistory?: boolean
	linksPrefetch?: boolean
	viewTransitions?: "default" | "none"
}

export default function Citation({
	value: citationValue,
	publicationId,
	format: citationFormat,
	type: citationType,
	linksReplaceHistory: citationLinksReplaceHistory = false,
	viewTransitions,
}: CitationProps) {
	const [copyCount, incCopyCount] = React.useReducer((x: number) => x + 1, 0)
	const [copyError, setCopyError] = React.useState<Error | undefined>()

	React.useEffect(() => {
		if (copyError) {
			const handler = () => setCopyError(undefined)
			document.body.addEventListener("click", handler)
			return () => document.body.removeEventListener("click", handler)
		}
	}, [copyError])

	return (
		<div>
			<Stack
				direction="row"
				justifyContent="center"
				alignItems="center"
				gap={{ smallDisplay: "medium", largeDisplay: "large" }}
			>
				{(Object.entries(formatEntries) as Entries<typeof formatEntries>).map(
					([formatId, formatEntry]) => {
						let isCurrent = formatId === citationFormat
						return (
							<FormatLink
								key={formatId}
								label={formatEntry.label}
								current={isCurrent}
								link={
									isCurrent
										? undefined
										: getCitePageURL(formatId, publicationId)
								}
								linkReplaceHistory={citationLinksReplaceHistory}
							/>
						)
					},
				)}
			</Stack>
			<div className={styles.citationContentWrapper}>
				<div
					className={styles.citationContent({
						state: copyError == null ? undefined : "hidden",
					})}
				>
					{citationType === "code" ? (
						<CodeBlock
							code={citationValue}
							flash={copyCount > 0}
							flashId={copyCount}
							viewTransitions={viewTransitions}
						/>
					) : (
						<TextCitation
							citation={citationValue}
							flash={copyCount > 0}
							flashId={copyCount}
							viewTransitions={viewTransitions}
						/>
					)}
				</div>
				{copyError == null ? null : (
					<div
						className={styles.errorMessage}
						role="alert"
						aria-label="Could not copy"
					>
						<Text align="center" color="inverse">
							Copying failed. Please copy the citation manually.
						</Text>
					</div>
				)}
			</div>
			<Stack
				direction="row"
				justifyContent="flex-end"
				className={styles.controls}
			>
				<Button
					icon={<CopyToClipboardIcon />}
					look="text"
					onClick={() => {
						copyTextToClipboard(citationValue)
							.then(incCopyCount)
							.catch((error) => {
								setCopyError(error)
								console.error(error)
							})
					}}
				>
					copy to clipboard
				</Button>
				<Button
					// Next links do not work well with data URLs: in particular, they
					// do not seem to work with the `download` attribute. So we use a
					// regular link instead.
					useHTMLLink
					icon={<DownloadIcon />}
					look="text"
					download={getDownloadFileName(citationFormat, publicationId)}
					href={getCitationDataURL(
						citationValue,
						citationFormat,
						publicationId,
					)}
				>
					download
				</Button>
			</Stack>
		</div>
	)
}

type FormatLabelProps = {
	label: string
	current: boolean
} & (
	| { link: string; linkReplaceHistory: boolean }
	| { link?: undefined; linkReplaceHistory?: boolean }
)
function FormatLink({
	label,
	link,
	current,
	linkReplaceHistory,
}: FormatLabelProps) {
	let state = current ? ("current" as const) : ("inactive" as const)
	if (link == null) {
		return (
			<div className={styles.formatLabelMenuItem({ state, type: "text" })}>
				<Text size="small">
					<div className={styles.formatLabel}>{label}</div>
				</Text>
			</div>
		)
	}
	return (
		<div className={styles.formatLabelMenuItem({ state, type: "link" })}>
			<Text size="small">
				<Link
					href={link}
					underline="never"
					highlight="none"
					scroll={false}
					replace={linkReplaceHistory}
				>
					<div className={styles.formatLabel}>{label}</div>
				</Link>
			</Text>
		</div>
	)
}

type TextCitationProps = {
	citation: string
	flash: boolean
	flashId?: unknown
	viewTransitions?: "default" | "none"
}
function TextCitation({
	citation,
	flash,
	flashId,
	viewTransitions,
}: TextCitationProps) {
	const ref = useTransientAnimation({
		className: styles.TextCitationFlashAnimation,
		isEnabled: flash,
		animId: flashId,
	})
	return (
		<QuoteBlock viewTransitions={viewTransitions}>
			<Text size="small">
				<span ref={ref}>{citation}</span>
			</Text>
		</QuoteBlock>
	)
}

function getDownloadFileName(
	citationFormat: keyof typeof formatEntries,
	publicationId: string,
) {
	return `${publicationId}.${formatEntries[citationFormat].fileExtension}`
}

function getCitationDataURL(
	citationValue: string,
	citationFormat: keyof typeof formatEntries,
	publicationId: string,
) {
	return `data:${internetMediaTypes[citationFormat]};name=${encodeURIComponent(
		getDownloadFileName(citationFormat, publicationId),
	)};charset=utf-16,${encodeURIComponent(citationValue)}`
}

const internetMediaTypes: Record<keyof typeof formatEntries, string> = {
	biblatex: "application/x-bibtex",
	enw: "application/x-endnote-refer",
	ris: "application/x-research-info-systems",
}
